import { SUPPORT_TICKET_FORM_URL } from 'Constants/env';
import type { PersonStoreState } from 'Modules/person/index.types';
import {
  REPORT_ISSUE_DEPARTMENT,
  REPORT_ISSUE_SUBJECT,
} from 'Modules/reportIssue/index.constants';

export type ReportIssuePrefillData = {
  accountName?: string | null;
  accountNumber?: string | number | null;
  firstName?: string | null;
  lastName?: string | null;
  userEmail?: string | null;
  phone?: string | null;
};

/**
 * Adds a query parameter when the value is non-empty.
 * Ignores null, undefined, empty strings, and their literal string values.
 */
const appendIfPresent = (
  params: URLSearchParams,
  key: string,
  value?: string | number | null
): void => {
  if (value === null || value === undefined) {
    return;
  }

  const stringValue = String(value).trim();

  if (
    !stringValue ||
    stringValue === 'undefined' ||
    stringValue === 'null'
  ) {
    return;
  }

  params.set(key, stringValue);
};

/**
 * Resolves the phone number for the logged-in user.
 * Prefers mobile, then the primary line, and finally the first line.
 */
export const resolveReportIssuePhone = (
  person: Pick<PersonStoreState, 'mobileNumber' | 'primaryLineId' | 'lines'>
): string | undefined => {
  if (person.mobileNumber?.trim()) {
    return person.mobileNumber.trim();
  }

  const primaryLine = person.lines?.find((line) => line.id === person.primaryLineId);
  if (primaryLine?.callerId?.trim()) {
    return primaryLine.callerId.trim();
  }

  const firstLine = person.lines?.[0];
  if (firstLine?.callerId?.trim()) {
    return firstLine.callerId.trim();
  }

  return undefined;
};

/**
 * Maps the logged-in user's person data to report-issue form fields.
 */
export const mapPersonToReportIssuePrefill = (
  person: PersonStoreState
): ReportIssuePrefillData => ({
  accountName: person.account?.name,
  accountNumber: person.account?.id,
  firstName: person.firstName,
  lastName: person.lastName,
  userEmail: person.email,
  phone: resolveReportIssuePhone(person),
});

/**
 * Builds the report-issue form URL with default and prefilled query parameters.
 */
export const buildReportIssueUrl = (
  prefill: ReportIssuePrefillData = {},
  subject: string = REPORT_ISSUE_SUBJECT
): string => {
  const params = new URLSearchParams();

  params.set('department', REPORT_ISSUE_DEPARTMENT);
  params.set('subject', subject);

  appendIfPresent(params, 'account_name', prefill.accountName);
  appendIfPresent(params, 'account_number', prefill.accountNumber);
  appendIfPresent(params, 'first_name', prefill.firstName);
  appendIfPresent(params, 'last_name', prefill.lastName);
  appendIfPresent(params, 'user_email', prefill.userEmail);
  appendIfPresent(params, 'phone', prefill.phone);

  return `${SUPPORT_TICKET_FORM_URL}?${params.toString()}`;
};
